import { _HTMLElement } from '../utils';

/**
 * Provides `connected` and `disconnected` callback alternatives to `connectedCallback` and `disconnectedCallback`, respectively.
 *
 * Many mixins override `connectedCallback` and `disconnectedCallback` to apply their behaviors. When consumers of such mixins make use of those hooks, they are required to first call `super` or otherwise lose whatever work the mixin(s) would perform.
 *
 * By providing the `connected` and `disconnected` callback alternatives, classes that extend from mixins won't need to worry about the complexity introduced in these callbacks by the mixins.
 *
 * [Partially lifted from SkateJS.](https://github.com/skatejs/skatejs/blob/5246f85abc166b8ffec3e87ce9c5775c425baf5a/packages/skatejs/src/with-lifecycle.js)
 * @category mixin
 * @param {Class} [Base=HTMLElement] Optional base class to extend from.
 * @returns {Class}
 * @example
   ```js
  import { withLifecycle } from '@updater-legacy/components/custom-element';

  class MyCustomElement extends withLifecycle() {
    connectedCallback() {
      // While flexible, being able to execute code prior to the `super` call
      // may introduce unexpected behavior when paired with mixins since they
      // need to make assumptions about order of operations.
      super.connectedCallback(); // Boilerplate-y and error-prone.
    }

    connected() {
      // No need to worry about order of operations or about calling `super`!
    }
  }
```
 */
export const withLifecycle = (Base = _HTMLElement) =>
  class extends Base {
    connectedCallback() {
      super.connectedCallback && super.connectedCallback();
      this.connected && this.connected();
    }

    disconnectedCallback() {
      super.disconnectedCallback && super.disconnectedCallback();
      this.disconnected && this.disconnected();
    }
  };
